<template>
  <div class="h-full w-full overflow-hidden bg-white">
    <img
      ref="pdfSlideImage"
      class="object-contain select-none transition-all w-full h-full pointer-events-none"
      :data-testid="`pdf-slide-image-${slide.order}`"
      :src="pdfUrl"
      :style="{ ...(contentStyle as CSSStyleValue) }"
    />
  </div>
</template>

<script lang="ts" setup>
import type { Slide } from '@/entities/deck'
import { SLIDE_READY_TIMEOUT } from '@/lib/constants/meetings'
import type { DeepReadonly } from 'vue'

const props = defineProps({
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    required: true,
  },
  contentStyle: {
    type: Object as PropType<Partial<CSSStyleDeclaration>>,
    default: () => ({}),
  },
  slideRefreshKey: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['slide-ready', 'update:image-dimensions', 'update:annotations', 'refetch-file'])
const pdfSlideImage = ref<HTMLImageElement | undefined>()

watch(
  toRef(props, 'slide'),
  (newSlide, lastSlide) => {
    if (newSlide.image?.id === lastSlide.image?.id) {
      emit('slide-ready', true)
    }
  },
  { flush: 'post' }
)

watch(
  toRef(props, 'slideRefreshKey'),
  () => {
    if (!props.slide.image?.id) {
      emit('refetch-file')
    }
  },
  { flush: 'post' }
)

useImageLoadEvent(pdfSlideImage, img => {
  emit('update:image-dimensions', { width: img.naturalWidth, height: img.naturalHeight })
  emit('update:annotations', { aspect: img.naturalWidth / img.naturalHeight })
  // Short timeout since it still takes a small amount of time for the image to actually show
  setTimeout(() => emit('slide-ready', true), SLIDE_READY_TIMEOUT)
})

const pdfUrl = computed(() => (props.slide.image?.id ? props.slide.image.url : props.slide.preview?.url))
</script>
